// ===== BEGIN FILE: src/components/report-v2/sections/BirthDetailsSection.tsx =====
/**
 * Birth details section with formatted information
 */

import React from 'react';
import { Box, Card, Typography, Chip } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';

// â”€â”€ Planet abbreviations & colors â”€â”€
const PLANET_ABBREV: Record<string, string> = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me',
  Jupiter: 'Ju', Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};
const PLANET_COLORS: Record<string, string> = {
  Sun: '#D97706', Moon: '#64748B', Mars: '#DC2626', Mercury: '#059669',
  Jupiter: '#EA580C', Venus: '#DB2777', Saturn: '#4F46E5', Rahu: '#7C3AED', Ketu: '#0D9488',
};

// â”€â”€ Zodiac element â†’ visual theme â”€â”€
const SIGN_ELEMENT: Record<string, 'fire' | 'earth' | 'air' | 'water'> = {
  Aries: 'fire', Leo: 'fire', Sagittarius: 'fire',
  Taurus: 'earth', Virgo: 'earth', Capricorn: 'earth',
  Gemini: 'air', Libra: 'air', Aquarius: 'air',
  Cancer: 'water', Scorpio: 'water', Pisces: 'water',
};
const SIGN_GLYPH: Record<string, string> = {
  Aries:       '\u2648', Taurus:      '\u2649', Gemini:      '\u264A', Cancer:    '\u264B',
  Leo:         '\u264C', Virgo:       '\u264D', Libra:       '\u264E', Scorpio:   '\u264F',
  Sagittarius: '\u2650', Capricorn:   '\u2651', Aquarius:    '\u2652', Pisces:    '\u2653',
};
const ELEMENT_STYLE: Record<string, { bg: string; accent: string; glyph: string; text: string }> = {
  fire:  { bg: 'rgba(220,60,20,0.09)',  accent: '#C2410C', glyph: '\uD83D\uDD25', text: '#9A3412' },
  earth: { bg: 'rgba(22,163,74,0.09)',  accent: '#15803D', glyph: '\uD83C\uDF31', text: '#166534' },
  air:   { bg: 'rgba(37,99,235,0.09)',  accent: '#1D4ED8', glyph: '\uD83D\uDCA8', text: '#1E40AF' },
  water: { bg: 'rgba(8,145,178,0.09)',  accent: '#0E7490', glyph: '\uD83D\uDCA7', text: '#155E75' },
};

// ── House meanings ──
const HOUSE_MEANING: Record<number, { title: string; keywords: string }> = {
  1:  { title: 'Self & Persona',       keywords: 'Body, Personality, Vitality' },
  2:  { title: 'Wealth & Family',      keywords: 'Speech, Savings, Early life' },
  3:  { title: 'Courage & Siblings',   keywords: 'Communication, Efforts' },
  4:  { title: 'Home & Comforts',      keywords: 'Mother, Property, Happiness' },
  5:  { title: 'Intellect & Children', keywords: 'Creativity, Education, Mantra' },
  6:  { title: 'Enemies & Illness',    keywords: 'Debt, Obstacles, Service' },
  7:  { title: 'Marriage & Partners',  keywords: 'Business, Foreign, Desires' },
  8:  { title: 'Longevity & Hidden',   keywords: 'Inheritance, Occult, Secrets' },
  9:  { title: 'Fortune & Dharma',     keywords: 'Father, Teacher, Pilgrimage' },
  10: { title: 'Career & Status',      keywords: 'Authority, Fame, Profession' },
  11: { title: 'Gains & Ambitions',    keywords: 'Income, Friendships, Goals' },
  12: { title: 'Liberation & Losses',  keywords: 'Expenses, Abroad, Moksha' },
};

// â”€â”€ Bhava Bala strength label â”€â”€
const getBhavaLabel = (pct: number) => {
  if (pct >= 80) return { label: 'Strong',  color: '#15803D' };
  if (pct >= 55) return { label: 'Average', color: '#B45309' };
  return               { label: 'Weak',    color: '#B91C1C' };
};

// House total score → label + color (mirrors D1 House Scores table)
const getHouseTotalLabel = (score: number) => {
  if (score >= 60)  return { label: 'Excellent', color: '#15803D' };
  if (score >= 35)  return { label: 'Strong',    color: '#16a34a' };
  if (score >= 15)  return { label: 'Average',   color: '#B45309' };
  if (score >= 0)   return { label: 'Weak',      color: '#EA580C' };
  return               { label: 'Stressed',   color: '#B91C1C' };
};

export const BirthDetailsSection: React.FC<ReportSectionProps & { phase?: 'top' | 'bottom' | 'all' }> = ({ data, config, phase = 'all', onNavigate }) => {
  const { birthDetails, houseScores } = data;

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ mb: 1 }}>
      <Typography level="body-sm" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.25, fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <Typography level="body-md" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
        {value}
      </Typography>
    </Box>
  );

  // Group aspectors by target house — show inline in each house card
  const aspectorsByHouse: Record<number, Array<{ planet: string; kind: string }>> = {};
  for (let h = 1; h <= 12; h++) aspectorsByHouse[h] = [];
  (birthDetails.planetaryAspects || []).forEach(asp => {
    aspectorsByHouse[asp.toHouse].push({ planet: asp.from, kind: asp.kind });
  });

  // Flat planet → functional nature map (for coloring aspecting planets)
  const planetFuncNatureMap: Record<string, 'benefic' | 'malefic' | 'neutral' | 'yoga_karaka'> = {};
  (birthDetails.planetsInHouses || []).forEach(({ planets }) => {
    planets.forEach(pl => { planetFuncNatureMap[pl.name] = pl.functionalNature; });
  });

  const hasBhavaBala = (birthDetails.houseCusps || []).some(h => h.bhavaPct !== undefined);

  return (
    <ReportSection
      title={phase === 'bottom' ? 'Chart Analysis & Predictions' : 'Birth Details'}
      icon={phase === 'bottom' ? '🔭' : '📋'}
      pageBreakBefore
      onNavigate={onNavigate ? () => onNavigate('calculator') : undefined}
      navigateLabel="View Chart →"
      navigateTab="calculator"
    >

      {/* ── TOP PHASE: Name banner + Personal Info + Chart Essentials ── */}
      {(phase === 'top' || phase === 'all') && (<>
      <Box sx={{ mb: 2.5, px: 2, pt: 1.5, pb: 1.25, borderLeft: '4px solid', borderLeftColor: 'primary.main', borderRadius: '0 8px 8px 0', background: 'linear-gradient(90deg, rgba(91,33,182,0.07) 0%, transparent 100%)' }}>
        <Typography level="h3" sx={{ fontWeight: 700, color: 'primary.main', textAlign: 'left', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
          {birthDetails.name}
        </Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.3 }}>
          Comprehensive Vedic Birth Chart Report and Analysis · Lahiri Ayanamsa
        </Typography>
      </Box>

      {/* â”€â”€ Personal Info + Technical Details â”€â”€ */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 2 }}>
        <Card variant="outlined" sx={{ p: 2 }}>
          <Typography level="title-sm" sx={{ mb: 1.5, color: 'primary.main' }}>Personal Information</Typography>
          <InfoRow label="Full Name" value={birthDetails.name} />
          <InfoRow label="Date of Birth" value={birthDetails.dateOfBirth} />
          <InfoRow label="Time of Birth" value={birthDetails.timeOfBirth} />
          <InfoRow label="Place of Birth" value={birthDetails.location} />
        </Card>
        <Card variant="outlined" sx={{ p: 2 }}>
          <Typography level="title-sm" sx={{ mb: 1.5, color: 'primary.main' }}>Technical Details</Typography>
          <InfoRow label="Coordinates" value={`${birthDetails.coordinates.lat.toFixed(2)}°N, ${birthDetails.coordinates.lon.toFixed(2)}°E`} />
          <InfoRow label="Timezone" value={birthDetails.timezone} />
          <InfoRow label="Ayanamsa" value={birthDetails.ayanamsa} />
        </Card>
      </Box>

      {/* ── Chart Essentials ── */}
      <Card variant="soft" sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, rgba(91,33,182,0.10), rgba(245,158,11,0.08))' }}>
        <Typography level="title-sm" sx={{ mb: 1.5, color: 'primary.main' }}>{String.fromCharCode(0x2728)} Chart Essentials</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <Box>
            <InfoRow label="Ascendant (Lagna)" value={birthDetails.ascendant} />
            <InfoRow label="Lagna Lord" value={birthDetails.ascendantLord} />
            <InfoRow label="Lagna Nakshatra" value={birthDetails.ascendantNakshatra || '--'} />
          </Box>
          <Box>
            <InfoRow label="Moon Sign (Rashi)" value={birthDetails.moonSign} />
            <InfoRow label="Moon Nakshatra (Birth Star)" value={birthDetails.moonNakshatra} />
          </Box>
        </Box>

        {/* Ascendant personality description */}
        {birthDetails.ascendantPersonality && (
          <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'rgba(91,33,182,0.18)' }}>
            <Typography level="body-xs" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {birthDetails.ascendant} Ascendant — Personality
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.8rem' }}>
              {birthDetails.ascendantPersonality}
            </Typography>
          </Box>
        )}

        {/* Moon nakshatra nature */}
        {birthDetails.moonNakshatraDesc && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'rgba(245,158,11,0.2)' }}>
            <Typography level="body-xs" sx={{ fontWeight: 700, color: '#B45309', mb: 0.5, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {birthDetails.moonNakshatra?.split(' - ')[0]} Moon — Nature & Temperament
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.8rem' }}>
              {birthDetails.moonNakshatraDesc}
            </Typography>
          </Box>
        )}
      </Card>
      {/* END TOP PHASE */}
      </>)}

      {/* ── BOTTOM PHASE: House Strength + Cumulative House Scores ── */}
      {(phase === 'bottom' || phase === 'all') && (<>
      {/* -- House Cusps with Sign Graphics & Bhava Bala -- */}
      {birthDetails.houseCusps && birthDetails.houseCusps.length > 0 && (
        <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
          {/* Header + Bhava Bala explanation */}
          <Box sx={{ mb: 1.5 }}>
            <Typography level="title-sm" sx={{ color: "primary.main", mb: 1.5 }}>
              House Strength & Zodiac Impact
            </Typography>
            {hasBhavaBala && (
              <Box sx={{ p: 1.25, borderRadius: "sm", bgcolor: "background.level1", border: "1px solid", borderColor: "divider", mt: 0.75 }}>
                <Typography level="body-xs" sx={{ fontWeight: 700, color: "text.primary", mb: 0.3, fontSize: "0.7rem" }}>
                  About Bhava Bala (House Strength)
                </Typography>
                <Typography level="body-xs" sx={{ color: "text.secondary", fontSize: "0.68rem", lineHeight: 1.6 }}>
                  Bhava Bala measures the total strength of each house, combining planet strength (Bhavadhipati), aspects, and Shadbala. Scores above 80% are strong; 55-80% average; below 55% weak.
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
            {birthDetails.houseCusps.map(({ house, sign, cusp, bhavaPct, isWholeSign, signNote }) => {
              const element = SIGN_ELEMENT[sign] || 'fire';
              const eStyle = ELEMENT_STYLE[element];
              const glyph = SIGN_GLYPH[sign] || '';
              const meaning = HOUSE_MEANING[house];
              const isLagna = house === 1;
              const bhavaInfo = bhavaPct !== undefined ? getBhavaLabel(bhavaPct) : null;

              return (
                <Box
                  key={house}
                  sx={{
                    position: 'relative',
                    borderRadius: 'sm',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: eStyle.bg,                    display: 'flex',
                    flexDirection: 'column',                  }}
                >
                  {/* Coloured sign glyph â€” decorative background watermark */}
                  <Box sx={{
                    position: 'absolute', right: 3, top: 2,
                    fontSize: '1.6rem', opacity: 0.18, lineHeight: 1, pointerEvents: 'none',
                    userSelect: 'none',
                  }}>
                    {glyph}
                  </Box>

                  <Box sx={{ p: 1, position: 'relative', zIndex: 1, flex: 1 }}>
                    {/* H# badge + Bhava % */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.3, flexWrap: 'wrap' }}>
                      <Typography sx={{
                        fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase',
                        letterSpacing: '0.07em', color: isLagna ? 'primary.main' : eStyle.accent,
                      }}>
                        H{house}
                      </Typography>
                      {bhavaInfo && (
                        <Typography sx={{
                          fontSize: '0.58rem', fontWeight: 700,
                          color: bhavaInfo.color,
                          bgcolor: `${bhavaInfo.color}18`,
                          px: 0.5, py: 0.1, borderRadius: '3px',
                          border: '1px solid', borderColor: `${bhavaInfo.color}44`,
                        }}>
                          {bhavaPct}% · {bhavaInfo.label}
                        </Typography>
                      )}
                    </Box>

                    {/* Sign name with glyph */}
                    <Typography sx={{
                      fontWeight: 700, fontSize: '0.77rem',
                      color: isLagna ? 'primary.main' : eStyle.text,
                      lineHeight: 1.2,
                    }}>
                      {glyph} {sign}
                    </Typography>

                    {/* House meaning */}
                    {meaning && (
                      <Typography sx={{
                        fontSize: '0.6rem', color: 'text.secondary',
                        lineHeight: 1.3, mt: 0.2, fontWeight: 600,
                      }}>
                        {meaning.title}
                      </Typography>
                    )}
                    {meaning && (
                      <Typography sx={{
                        fontSize: '0.56rem', color: 'text.tertiary',
                        lineHeight: 1.3, mt: 0.1, fontStyle: 'italic',
                      }}>
                        {meaning.keywords}
                      </Typography>
                    )}

                    {/* Cusp degree — H1 only: actual ascending degree within sign */}
                    {isLagna && cusp ? (
                      <Box sx={{ mt: 0.6 }}>
                        <Typography sx={{
                          fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: 700,
                          color: 'primary.main',
                          bgcolor: 'rgba(91,33,182,0.08)',
                          px: 0.6, py: 0.15, borderRadius: '4px',
                          display: 'inline-block',
                        }}>
                          {cusp}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: '0.5rem', color: 'text.tertiary', ml: 0.4 }}>
                          Asc
                        </Typography>
                      </Box>
                    ) : !isLagna ? (
                      <Typography sx={{ fontSize: '0.5rem', color: 'text.tertiary', fontStyle: 'italic', mt: 0.6 }}>
                        whole sign
                      </Typography>
                    ) : null}

                    {/* Sign description from Supabase houses table */}
                    {signNote && (
                      <Typography sx={{
                        fontSize: '0.68rem', color: 'text.secondary',
                        lineHeight: 1.5, mt: 0.6, fontStyle: 'italic',
                        borderTop: '1px solid', borderColor: 'divider', pt: 0.5,
                      }}>
                        {signNote}
                      </Typography>
                    )}
                  </Box>

                  {/* Bottom element strip */}
                  <Box sx={{ height: '3px', bgcolor: eStyle.accent, opacity: 0.6 }} />
                </Box>
              );
            })}
          </Box>

          {/* Element legend */}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
            {(['fire', 'earth', 'air', 'water'] as const).map(el => (
              <Box key={el} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: ELEMENT_STYLE[el].accent }} />
                <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', textTransform: 'capitalize' }}>
                  {ELEMENT_STYLE[el].glyph} {el}
                </Typography>
              </Box>
            ))}
            <Typography sx={{ fontSize: '0.62rem', color: 'text.tertiary', fontStyle: 'italic', ml: 0.5 }}>
              Whole-sign system: H2-H12 begin at 0° of their sign. Only the Ascendant (H1) has a unique degree.
            </Typography>
          </Box>
        </Card>
      )}

      {/* â”€â”€ Planets in Houses â”€â”€ */}
      {birthDetails.planetsInHouses && birthDetails.planetsInHouses.length > 0 && (() => {
        // Flatten all planets with notes for interpretations section
        const allPlanetsWithNotes = birthDetails.planetsInHouses!.flatMap(({ house, sign, planets }) =>
          planets.filter(pl => pl.pihNote).map(pl => ({ ...pl, house, sign }))
        );
        return (
          <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
            {/* ── Part 1: Compact 4-col reference grid ── */}
            <Box sx={{ mb: 1.5 }}>
              <Typography level="title-sm" sx={{ color: 'primary.main', mb: 1.5 }}>
                {String.fromCharCode(0xD83E, 0xDE90)} Cumulative House Scores & Planet Impact
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                <Box component="span" sx={{ color: '#16a34a', fontWeight: 700 }}>Green</Box> = Benefic / Yoga Karaka ·{' '}
                <Box component="span" sx={{ color: '#dc2626', fontWeight: 700 }}>Red</Box> = Malefic ·{' '}
                <Box component="span" sx={{ color: '#d4b106', fontWeight: 700 }}>Amber</Box> = Neutral · % = blended strength
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
              {birthDetails.planetsInHouses!.map(({ house, sign, planets, lihPosition, lihNote }) => {
                const element = SIGN_ELEMENT[sign] || 'fire';
                const eStyle = ELEMENT_STYLE[element];
                const glyph = SIGN_GLYPH[sign] || '';
                const meaning = HOUSE_MEANING[house];
                const isLagna = house === 1;
                return (
                  <Box
                    key={house}
                    sx={{
                      position: 'relative',
                      borderRadius: 'sm',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: eStyle.bg,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Watermark glyph */}
                    <Box sx={{
                      position: 'absolute', right: 3, top: 2,
                      fontSize: '1.6rem', opacity: 0.18, lineHeight: 1,
                      pointerEvents: 'none', userSelect: 'none',
                    }}>
                      {glyph}
                    </Box>
                    <Box sx={{ p: 1, position: 'relative', zIndex: 1, flex: 1 }}>
                    {/* H# + House Total score — inline on same row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.3, flexWrap: 'nowrap' }}>
                      <Typography sx={{
                        fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase',
                        letterSpacing: '0.07em', flexShrink: 0,
                        color: isLagna ? 'primary.main' : eStyle.accent,
                      }}>
                        H{house}
                      </Typography>
                      {houseScores[house] !== undefined && (() => {
                        const htInfo = getHouseTotalLabel(houseScores[house]);
                        return (
                          <Typography sx={{
                            fontSize: '0.52rem', fontWeight: 800, color: htInfo.color,
                            bgcolor: `${htInfo.color}1a`, px: 0.4, py: 0.05,
                            borderRadius: '3px', border: '1px solid', borderColor: `${htInfo.color}44`,
                            lineHeight: 1.5, flexShrink: 0,
                          }}>
                            {houseScores[house] > 0 ? '+' : ''}{Math.round(houseScores[house])} · {htInfo.label}
                          </Typography>
                        );
                      })()}
                    </Box>
                    {/* Sign name */}
                    <Typography sx={{
                      fontWeight: 700, fontSize: '0.77rem',
                      color: isLagna ? 'primary.main' : eStyle.text,
                      lineHeight: 1.2,
                    }}>
                      {glyph} {sign}
                    </Typography>
                    {/* House meaning */}
                    {meaning && (
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.3, mt: 0.2, fontWeight: 600 }}>
                        {meaning.title}
                      </Typography>
                    )}
                    {/* Residents — solid-fill pills */}
                    {planets.length > 0 && (
                      <>
                        <Typography sx={{ fontSize: '0.5rem', color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: '0.07em', mt: 0.6, mb: 0.25 }}>Residents</Typography>
                        <Box sx={{ mt: 0, display: 'flex', flexWrap: 'wrap', gap: 0.35 }}>
                          {planets.map(pl => {
                            const funcColor = (pl.functionalNature === 'benefic' || pl.functionalNature === 'yoga_karaka')
                              ? '#16a34a' : pl.functionalNature === 'malefic' ? '#dc2626' : '#d4b106';
                            const pctColor = pl.blendedPct !== undefined
                              ? (pl.blendedPct >= 0 ? '#15803D' : '#B91C1C') : '#64748b';
                            return (
                              <Box key={pl.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <Typography sx={{
                                  px: 0.6, py: 0.05, borderRadius: '4px',
                                  fontSize: '0.65rem', fontWeight: 700,
                                  color: '#fff', bgcolor: funcColor, lineHeight: 1.7,
                                }}>
                                  {PLANET_ABBREV[pl.name] || pl.name.slice(0, 2)}
                                </Typography>
                                {pl.blendedPct !== undefined && (
                                  <Typography sx={{
                                    fontSize: '0.58rem', fontWeight: 700, color: pctColor,
                                    bgcolor: `${pctColor}15`, px: 0.4, py: 0.05,
                                    borderRadius: '3px', border: '1px solid',
                                    borderColor: `${pctColor}40`, lineHeight: 1.6,
                                  }}>
                                    {pl.blendedPct > 0 ? '+' : ''}{Math.round(pl.blendedPct)}%
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </>
                    )}
                    {/* Aspectors — outlined pills separated by dashed rule */}
                    {aspectorsByHouse[house]?.length > 0 && (
                      <Box sx={{ mt: 0.6, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                        <Typography sx={{ fontSize: '0.5rem', color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.25 }}>Aspects</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.35 }}>
                          {aspectorsByHouse[house].map((asp, i) => {
                            const nature = planetFuncNatureMap[asp.planet] || 'neutral';
                            const funcColor = (nature === 'benefic' || nature === 'yoga_karaka')
                              ? '#16a34a' : nature === 'malefic' ? '#dc2626' : '#d4b106';
                            const kindShort = asp.kind.replace(' aspect', '');
                            return (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                <Typography sx={{
                                  px: 0.6, py: 0.05, borderRadius: '4px',
                                  fontSize: '0.65rem', fontWeight: 700,
                                  color: funcColor,
                                  bgcolor: `${funcColor}15`,
                                  border: '1px solid', borderColor: `${funcColor}55`,
                                  lineHeight: 1.7,
                                }}>
                                  {PLANET_ABBREV[asp.planet] || asp.planet.slice(0, 2)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.5rem', color: 'text.tertiary', lineHeight: 1 }}>
                                  {kindShort}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                    {/* Empty — no residents, no aspectors */}
                    {planets.length === 0 && (!aspectorsByHouse[house] || aspectorsByHouse[house].length === 0) && (
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.tertiary', fontStyle: 'italic', mt: 0.5 }}>
                        — Empty
                      </Typography>
                    )}
                    {/* Lord-in-house interpretation */}
                    {(lihPosition || lihNote) && (
                      <Box sx={{ mt: 0.7, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        {lihPosition && (
                          <Typography sx={{ fontSize: '0.52rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: eStyle.accent, mb: 0.2 }}>
                            {lihPosition}
                          </Typography>
                        )}
                        {lihNote && (
                          <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1.55, fontStyle: 'italic' }}>
                            {lihNote}
                          </Typography>
                        )}
                      </Box>
                    )}
                    </Box>
                    {/* Bottom element strip */}
                    <Box sx={{ height: '3px', bgcolor: eStyle.accent, opacity: 0.6 }} />
                  </Box>
                );
              })}
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
              {([['#16a34a', 'Benefic / Yoga Karaka'], ['#dc2626', 'Malefic'], ['#d4b106', 'Neutral']] as const).map(([color, label]) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
                  <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>{label}</Typography>
                </Box>
              ))}
              <Typography sx={{ fontSize: '0.62rem', color: 'text.tertiary', fontStyle: 'italic', ml: 0.5 }}>
                % = blended planet strength (Shadbala + aspects + dignity)
              </Typography>
            </Box>
          </Card>
        );
      })()}
      {/* END BOTTOM PHASE */}
      </>)}

    </ReportSection>
  );
};
